import * as React from 'react';

import { useAuth } from '@/features/auth';
import type { Permission } from '@/shared/constants/permissions';
import type { UUID } from '@/shared/types/api';

import { useMyAccess } from '../api/access.queries';
import {
  accessProfileOf,
  hasPermission,
  hasPermissionAnywhere,
  isUnitMember,
  scopeIdsOfType,
} from '../lib/authorization';
import type { AccessProfile, MyAccess, ScopeChain } from '../types/access.types';

interface AuthorizationContextValue {
  access: MyAccess | null;
  isLoading: boolean;
  error: unknown;
  profile: AccessProfile;
  isSuperAdmin: boolean;
  /** Does the user hold `permission` over this exact resource? */
  can: (permission: Permission, scope?: ScopeChain) => boolean;
  /** Does the user hold `permission` anywhere? Use to reveal whole sections. */
  canAnywhere: (permission: Permission) => boolean;
  /** Does the user live in this unit? */
  livesIn: (unitId: UUID) => boolean;
  /** Complexes/buildings/units the user holds a direct grant on. */
  scopeIds: (scopeType: 'complex' | 'building' | 'unit') => UUID[];
  /** Units the user lives in. */
  memberUnitIds: UUID[];
}

const AuthorizationContext = React.createContext<AuthorizationContextValue | null>(null);

/**
 * Resolves `/users/me/access` once and exposes permission checks to the tree.
 *
 * Provided at the app root rather than per-screen so a permission check costs
 * nothing: every `can()` reads the same cached payload.
 */
export function AuthorizationProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const accessQuery = useMyAccess({ enabled: isAuthenticated });
  const access = accessQuery.data ?? null;

  const value = React.useMemo<AuthorizationContextValue>(
    () => ({
      access,
      isLoading: isAuthenticated && accessQuery.isPending,
      error: accessQuery.error,
      profile: accessProfileOf(access),
      isSuperAdmin: access?.is_superadmin ?? false,
      can: (permission, scope) => hasPermission(access, permission, scope),
      canAnywhere: (permission) => hasPermissionAnywhere(access, permission),
      livesIn: (unitId) => isUnitMember(access, unitId),
      scopeIds: (scopeType) => scopeIdsOfType(access, scopeType),
      memberUnitIds: access?.memberships.map((membership) => membership.unit_id) ?? [],
    }),
    [access, accessQuery.error, accessQuery.isPending, isAuthenticated],
  );

  return <AuthorizationContext value={value}>{children}</AuthorizationContext>;
}

export function useAuthorization(): AuthorizationContextValue {
  const context = React.use(AuthorizationContext);
  if (!context) {
    throw new Error('useAuthorization must be used within an <AuthorizationProvider>');
  }
  return context;
}
